import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MatDialogActions, MatDialogClose, MatDialogTitle, MatDialogContent } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';

import { WarrantyData } from '../../../../models/sol-estimate-table/WarrantyData';
import { WarrantyFormGroup, WarrantyRowFormGroup } from '../../../../models/sol-estimate-table/WarrantyFormGroup';
import { MultilingualCurrencyPipe } from '../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { CONSTANTS } from '../../../../Constants/Constants';
import { SolEstimateMaterialService } from '../../../../services/estimate/sol-estimate-material.service';
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';
import { SolMaterialEscalationType } from '../../../../models/enum';
import { NumericMaskDirective } from '../../../../shared/numeric/numeric-mask.directive';
import { NumericFieldConfig } from '../../../../shared/numeric/numeric.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-sol-estimate-warranty-dialog',
  imports: [
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    TranslatePipe,
    MatButtonModule,
    MatTableModule,
    MatFormField,
    MatInput,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MultilingualCurrencyPipe,
    NumericMaskDirective,
    CloseOnEscapeDirective
  ],
  templateUrl: './sol-estimate-warranty-dialog.component.html',
  styleUrl: './sol-estimate-warranty-dialog.component.scss'
})
export class SolEstimateWarrantyDialogComponent implements OnInit {

  private readonly destroyRef = inject(DestroyRef);
  warrantyDataSource: WarrantyData[];
  displayedColumns: string[] = ['warrantyPeriodName', 'year1Rate', 'year2Rate', 'year3Rate', 'year4Rate', 'year5Rate', 'total'];
  yearDateColumns: string[] = ['nameDate', 'year1Date', 'year2Date', 'year3Date', 'year4Date', 'year5Date', 'totalDate'];
  warrantyFormGroup!: FormGroup<WarrantyFormGroup>;
  readonly formGroupNames = [
    CONSTANTS.WARRANTY_FORM_GROUP_SIEMENS,
    CONSTANTS.WARRANTY_FORM_GROUP_OUTSIDE,
    CONSTANTS.WARRANTY_FORM_GROUP_MECHANICAL,
    CONSTANTS.WARRANTY_FORM_GROUP_ELECTRICAL
  ];
  totalWarrantyCost: number = CONSTANTS.ZERO;
  isInputsLocked: boolean;
  jobEndDate: Date | null;
  jobStartDate: Date | null;

  /** Precision follows the app-wide decimal preference, same as the legacy directives. */
  readonly rateConfig: NumericFieldConfig = {
    kind: 'percentage',
    allowNegative: false,
    max: CONSTANTS.HUNDRED,
    useGrouping: false,
    step: CONSTANTS.ZERO_POINT_ONE,
    revertToLastValidValue: true
  };

  readonly sellPriceConfig: NumericFieldConfig = {
    kind: 'currency',
    allowNegative: false,
    max: CONSTANTS.SHIPPING_SELL_PRICE_MAX_VALUE,
    useGrouping: true,
    showStepper: false,
    step: CONSTANTS.ONE,
    revertToLastValidValue: true
  };

  /**
   * Column-header labels for the five warranty years.
   * Derived from jobEndDate — each label is jobEndDate offset by +0 to +4 years.
   * Falls back to 'Year 1'…'Year 5' when no valid date is available.
   */
  yearHeaders: string[] = Array.from({ length: CONSTANTS.FIVE }, (_, i) => `${CONSTANTS.YEAR_HEADER_DEFAULT_PREFIX}${i + 1}`);

  /**
   * The five percentage rate control names — shared across sync, disable/enable, and recalculate helpers
   * to eliminate duplicate string literals.
   */
  private readonly rateControlNames: readonly string[] = [
    CONSTANTS.WARRANTY_RATE_YEAR1, CONSTANTS.WARRANTY_RATE_YEAR2, CONSTANTS.WARRANTY_RATE_YEAR3,
    CONSTANTS.WARRANTY_RATE_YEAR4, CONSTANTS.WARRANTY_RATE_YEAR5
  ];

  /**
   * Year-2 through year-5 control names — used when zeroing non-first-year rates on direct dollar entry.
   */
  private readonly nonFirstYearRateControlNames: readonly string[] = [
    CONSTANTS.WARRANTY_RATE_YEAR2, CONSTANTS.WARRANTY_RATE_YEAR3,
    CONSTANTS.WARRANTY_RATE_YEAR4, CONSTANTS.WARRANTY_RATE_YEAR5
  ];

  /** Rate controls plus the total control — used when enabling/disabling locked rows. */
  private readonly rateAndTotalControlNames: readonly string[] = [
    CONSTANTS.WARRANTY_RATE_YEAR1, CONSTANTS.WARRANTY_RATE_YEAR2, CONSTANTS.WARRANTY_RATE_YEAR3,
    CONSTANTS.WARRANTY_RATE_YEAR4, CONSTANTS.WARRANTY_RATE_YEAR5, CONSTANTS.WARRANTY_RATE_TOTAL
  ];

  /** Index of the row whose total cell is currently in edit mode; null when none. */
  editingTotalIndex: number | null = null;


  constructor(
    public dialogRef: MatDialogRef<SolEstimateWarrantyDialogComponent>,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly materialService: SolEstimateMaterialService
  ) { }

  ngOnInit(): void {
    this.warrantyDataSource = structuredClone(this.materialService.getWarrantyData());
    this.isInputsLocked = this.materialService.getWarrantyLocked();
    this.jobEndDate = this.materialService.getJobEndDate();
    this.jobStartDate = this.materialService.getJobStartDate();
    this.computeYearHeaders();
    this.initializeFormGroup();
    this.subscribeToWarrantyFormGroupChanges();
  }

  // ---------------------------------------------------------------------------
  // Initialisation
  // ---------------------------------------------------------------------------

  private computeYearHeaders(): void {
    if (!this.jobEndDate) {
      return;
    }

    const base = new Date(this.jobEndDate);
    if (Number.isNaN(base.getTime())) {
      return;
    }

    this.yearHeaders = Array.from({ length: CONSTANTS.FIVE }, (_, offset) => {
      const d = new Date(base.getFullYear() + offset, base.getMonth(), base.getDate());
      return d.toLocaleDateString(CONSTANTS.LANGUAGE_EN_US, { month: 'short', day: 'numeric', year: 'numeric' });
    });
  }

  private initializeFormGroup(): void {
    this.warrantyFormGroup = this.formBuilder.group<WarrantyFormGroup>({
      siemensMaterialGroup: this.buildRowFormGroup(CONSTANTS.ZERO),
      outsideMaterialGroup: this.buildRowFormGroup(CONSTANTS.ONE),
      mechanicalMaterialGroup: this.buildRowFormGroup(CONSTANTS.TWO),
      electricalMaterialGroup: this.buildRowFormGroup(CONSTANTS.THREE)
    });

    this.updateTotalWarrantyCost();
    this.applyLockState();
  }

  private buildRowFormGroup(index: number): FormGroup<WarrantyRowFormGroup> {
    const row = this.warrantyDataSource[index];
    const total = this.materialService.calculateWarrantyTotal(
      row.dollars, row.year1Rate, row.year2Rate, row.year3Rate, row.year4Rate, row.year5Rate,
      this.getMaterialCategory(index)
    );
    this.warrantyDataSource[index].total = total;

    return this.formBuilder.group<WarrantyRowFormGroup>({
      warrantyPeriodName: this.formBuilder.control<string>(row.warrantyPeriodName),
      dollars: this.formBuilder.control<number>(row.dollars),
      year1Rate: this.formBuilder.control<number>(row.year1Rate),
      year2Rate: this.formBuilder.control<number>(row.year2Rate),
      year3Rate: this.formBuilder.control<number>(row.year3Rate),
      year4Rate: this.formBuilder.control<number>(row.year4Rate),
      year5Rate: this.formBuilder.control<number>(row.year5Rate),
      total: this.formBuilder.control<number>(total)
    });
  }

  private subscribeToWarrantyFormGroupChanges(): void {
    this.formGroupNames.forEach((_groupName, index) => {
      const rowGroup = this.getRowGroup(index);

      // Per-rate subscriptions keep a direct total edit from being recalculated away.
      this.rateControlNames.forEach(rateName => {
        rowGroup.get(rateName)?.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.onRateChange(index));
      });
      rowGroup.controls['total'].valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.onTotalChange(index);
        });
    });
  }
  // ---------------------------------------------------------------------------
  // Lock / Unlock
  // ---------------------------------------------------------------------------

  private updateTotalWarrantyCost(): void {
    this.totalWarrantyCost = this.formGroupNames.reduce((sum, _groupName, index) => {
      const group = this.getRowGroup(index);
      return sum + (group.controls.total.value || CONSTANTS.ZERO);
    }, CONSTANTS.ZERO);
  }

  private applyLockState(): void {
    if (this.isInputsLocked) {
      this.syncLockedRates();
      this.setNonSiemensRateControlsState(false);
    }
  }

  lockInputs(): void {
    this.isInputsLocked = !this.isInputsLocked;

    if (this.isInputsLocked) {
      this.syncLockedRates();
      this.setNonSiemensRateControlsState(false);
    } else {
      this.setNonSiemensRateControlsState(true);
    }
  }

  private syncLockedRates(): void {
    const siemensGroup = this.getRowGroup(CONSTANTS.ZERO);

    this.formGroupNames.slice(CONSTANTS.ONE).forEach((_groupName, idx) => {
      const dataIndex = idx + CONSTANTS.ONE;
      const group = this.getRowGroup(dataIndex);

      this.rateControlNames.forEach(rateName => {
        const siemensRate = siemensGroup.get(rateName)?.value || CONSTANTS.ZERO;
        group.get(rateName)?.setValue(siemensRate, { emitEvent: false });
        (this.warrantyDataSource[dataIndex] as unknown as Record<string, number>)[rateName] = siemensRate;
      });

      this.recalculateRowTotal(dataIndex);
    });

    this.updateTotalWarrantyCost();
  }

  /**
   * Enables or disables all rate and total controls for Outside, Mechanical and Electrical rows.
   * Replaces the previously duplicated disableNonSiemensRateControls / enableNonSiemensRateControls methods.
   */
  private setNonSiemensRateControlsState(enabled: boolean): void {
    this.formGroupNames.slice(CONSTANTS.ONE).forEach((_groupName, idx) => {
      const group = this.getRowGroup(idx + CONSTANTS.ONE);
      this.rateAndTotalControlNames.forEach(ctrl => {
        if (enabled) {
          group.get(ctrl)?.enable({ emitEvent: false });
        } else {
          group.get(ctrl)?.disable({ emitEvent: false });
        }
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Change handlers
  // ---------------------------------------------------------------------------

  /**
   * Called when a percentage rate input changes.
   * Recalculates the row total; if locked propagates Siemens rates to all other categories.
   */
  onRateChange(index: number): void {
    this.recalculateRowTotal(index);

    if (this.isInputsLocked && index === CONSTANTS.ZERO) {
      this.syncLockedRates();
    }

    this.updateTotalWarrantyCost();
  }

  /**
   * Called when the dollar-total field is edited directly (plan §4.4).
   * Reverse-calculates year-1 rate from the entered total; zeros out years 2–5.
   */
  onTotalChange(index: number): void {
    const group = this.getRowGroup(index);
    const dollars = group.controls.dollars.value || CONSTANTS.ZERO;
    const enteredTotal = group.controls.total.value || CONSTANTS.ZERO;

    const year1Rate = this.materialService.calculateWarrantyYear1RateFromTotal(enteredTotal, dollars);

    group.controls.year1Rate.setValue(year1Rate, { emitEvent: false });
    this.clearNonFirstYearRates(group, this.warrantyDataSource[index]);

    const row = this.warrantyDataSource[index];
    row.year1Rate = year1Rate;
    row.total = enteredTotal;

    this.updateTotalWarrantyCost();
  }

  startEditingTotal(index: number): void {
    if (!this.isTotalDisabled(index)) {
      this.editingTotalIndex = index;
    }
  }

  stopEditingTotal(): void {
    this.editingTotalIndex = null;
  }

  isTotalDisabled(index: number): boolean {
    return this.isInputsLocked && index !== CONSTANTS.ZERO;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------


  private getMaterialCategory(index: number): SolMaterialEscalationType {
    return Object.values(SolMaterialEscalationType)[index];
  }

  private recalculateRowTotal(index: number): void {
    const group = this.getRowGroup(index);
    const dollars = group.controls.dollars.value || CONSTANTS.ZERO;
    const total = this.materialService.calculateWarrantyTotal(
      dollars,
      group.controls.year1Rate.value,
      group.controls.year2Rate.value,
      group.controls.year3Rate.value,
      group.controls.year4Rate.value,
      group.controls.year5Rate.value,
      this.getMaterialCategory(index)
    );
    group.controls.total.setValue(total, { emitEvent: false });

    const row = this.warrantyDataSource[index];
    row.total = total;
    row.year1Rate = group.controls.year1Rate.value;
    row.year2Rate = group.controls.year2Rate.value;
    row.year3Rate = group.controls.year3Rate.value;
    row.year4Rate = group.controls.year4Rate.value;
    row.year5Rate = group.controls.year5Rate.value;
  }

  /**
   * Zeros years 2–5 on both the form controls and the backing data row.
   */
  private clearNonFirstYearRates(group: FormGroup<WarrantyRowFormGroup>, row: WarrantyData): void {
    this.nonFirstYearRateControlNames.forEach(rateName => {
      group.get(rateName)?.setValue(CONSTANTS.ZERO, { emitEvent: false });
      (row as unknown as Record<string, number>)[rateName] = CONSTANTS.ZERO;
    });
  }

  private getRowGroup(index: number): FormGroup<WarrantyRowFormGroup> {
    return this.warrantyFormGroup.get(this.formGroupNames[index]) as FormGroup<WarrantyRowFormGroup>;
  }

  getFormGroupName(index: number): string {
    return this.formGroupNames[index];
  }

  /** TrackBy function for mat-table rows. */
  trackByIndex(index: number): number {
    return index;
  }

  /** Returns the computed total value for a row, used in the template to avoid deep chain expressions. */
  getTotalValue(index: number): number {
    const group = this.getRowGroup(index);
    return group.controls.total.value || CONSTANTS.ZERO;
  }

  // ---------------------------------------------------------------------------
  // Dialog actions
  // ---------------------------------------------------------------------------

  onSave(): void {
    if (this.warrantyFormGroup.valid) {
      this.dialogRef.close({ data: this.warrantyDataSource, locked: this.isInputsLocked });
    }
  }

}

